import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { ServiceType } from '../models/service-type';
import { EpApplicationService, EpErpRestService } from '@epicor/ux-platform';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { Efx } from '../models/functions';
import { HelpPageType, HelpPageTypes } from '../models/help-page-types';
import { ErpService } from './erp.service';
import { ContextService } from './context.service';

@Injectable({
  providedIn: 'root'
})
export class HelpPageService extends ErpService {
  protected serviceName: string;
  private helpPathTypeCache$: Observable<HelpPageType>;

  constructor(
    private http: HttpClient, 
    private epApplicationService: EpApplicationService,
    epErpRestService: EpErpRestService,
    contextService: ContextService
  ) {
    super(epErpRestService, contextService);
  }

  public getServices(serviceType: ServiceType): Observable<string[]> {
    const token = this.epApplicationService.tokenService.token;

    // check token!
    if (!this.epApplicationService.tokenService.hasToken()) {
      throw new Error('No token found.');
    }

    return this.http
      .get<any>(
        this.epApplicationService.sessionService.serverUrl + '/api/helppage/services?serviceType=' + serviceType,
        {
          headers: { Authorization: 'Bearer ' + token }
        }
      )
      .pipe(catchError(this.handleError('services', [])));
  }

  public getCompanies(): Observable<string[]> {
    const token = this.epApplicationService.tokenService.token;

    // check token!
    if (!this.epApplicationService.tokenService.hasToken()) {
      throw new Error('No token found.');
    }

    return this.http
      .get<any>(`${this.epApplicationService.sessionService.serverUrl}/api/helppage/companies`, {
        headers: { Authorization: 'Bearer ' + token }
      })
      .pipe(catchError(this.handleError('companies', [])));
  }

  public getHelpPageType(): Observable<HelpPageType> {
    if (!this.helpPathTypeCache$) {
      this.helpPathTypeCache$ = this.requestHelpPageType().pipe(
        shareReplay(1) // cache the value, because it does not change
      );
    }
    return this.helpPathTypeCache$;
  }

  private requestHelpPageType(): Observable<HelpPageType> {
    const token = this.epApplicationService.tokenService.token;

    // check token!
    if (!this.epApplicationService.tokenService.hasToken()) {
      throw new Error('No token found.');
    }

    return this.http
      .get<HelpPageType>(`${this.epApplicationService.sessionService.serverUrl}/api/helppage/helpPageType`, {
        headers: { Authorization: 'Bearer ' + token }
      })
      .pipe(catchError(err => of({ helpPageType: HelpPageTypes.Swagger })));
  }

  public getBaqs(companyId: string, queryIdSubstring: string): Observable<any> {
    // check token!
    if (!this.epApplicationService.tokenService.hasToken()) {
      throw new Error('No token found.');
    }
    const token = this.epApplicationService.tokenService.token;
    this.epErpRestService.currentSettings.company = companyId;
    const params = {
      $top: '100',
      $filter: `indexof(QueryID,'${encodeURIComponent(queryIdSubstring)}') ne 0`
    };
    this.serviceName = 'Ice.BO.BAQDesignerSvc';

    return this.get('List', { params }).pipe(
      map(response => Array.isArray(response) ? response : []), // Ensure response is always an array
    );
  }

  public getFunctions(companyId: string): Observable<Efx[]> {
    const token = this.epApplicationService.tokenService.token;

    // check token!
    if (!this.epApplicationService.tokenService.hasToken()) {
      throw new Error('No token found.');
    }

    return this.http
      .get<any>(`${this.epApplicationService.sessionService.serverUrl}/api/v2/efx/${companyId}/`, {
        headers: { Authorization: 'Bearer ' + token }
      })
      .pipe(catchError(this.handleError('functions', [])));
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // console.error(error);
      return throwError(error);
    };
  }
}
